import {
  email,
  required,
  useGetIdentity,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useWatch } from "react-hook-form";
import { BooleanInput } from "@/components/admin/boolean-input";
import { TextInput } from "@/components/admin/text-input";
import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";

import type { Sale } from "../types";
import type { BvFormFields } from "./bvAccess";
import { TagsListEdit } from "../contacts/TagsListEdit";
import { FRAKTIONEN } from "../misc/fraktionen";

export function SalesInputs({ showTags = false }: { showTags?: boolean }) {
  const { identity } = useGetIdentity();
  const record = useRecordContext<Sale & BvFormFields>();
  const translate = useTranslate();
  const bvMode = useWatch({ name: "bv_mode" });
  const isSuperadmin = identity?.superadmin === true;
  // Full BV access opens every non-public Drucksache: only superadmins grant or revoke it.
  const bvChoices = [
    { id: "none", name: translate("resources.sales.bv.none", { _: "None" }) },
    {
      id: "list",
      name: translate("resources.sales.bv.list", { _: "Selected Gremien" }),
    },
    {
      id: "admin",
      name: translate("resources.sales.bv.admin", { _: "Full access" }),
      disabled: !isSuperadmin,
    },
  ];
  return (
    <div className="space-y-4 w-full">
      <TextInput source="first_name" validate={required()} helperText={false} />
      <TextInput source="last_name" validate={required()} helperText={false} />
      <TextInput
        source="email"
        validate={[required(), email()]}
        helperText={false}
      />
      <TextInput source="phone" helperText={false} />
      <DateInput source="birthday" helperText={false} />
      <TextInput source="address" multiline helperText={false} />
      {showTags && <TagsListEdit resource="sales" tagResource="sales_tags" />}
      <SelectInput
        source="fraktion"
        choices={[...FRAKTIONEN]}
        optionText="label"
        optionValue="value"
        validate={required()}
        // Only superadmins move accounts between Fraktionen; admins add to their own.
        defaultValue={isSuperadmin ? undefined : identity?.fraktion}
        readOnly={record?.id === identity?.id || !isSuperadmin}
        helperText={false}
      />
      <BooleanInput
        source="administrator"
        readOnly={record?.id === identity?.id}
        helperText={false}
      />
      {identity?.superadmin === true && (
        <BooleanInput
          source="superadmin"
          readOnly={record?.id === identity?.id}
          helperText={false}
        />
      )}
      <BooleanInput
        source="disabled"
        readOnly={record?.id === identity?.id}
        helperText={false}
      />
      <SelectInput
        source="bv_mode"
        choices={bvChoices}
        defaultValue="none"
        readOnly={!isSuperadmin && record?.bv_mode === "admin"}
        helperText={false}
      />
      {bvMode === "list" && (
        <TextInput
          source="bv_list"
          multiline
          validate={required()}
          helperText={translate("resources.sales.bv.list_help", {
            _: "One Gremium per line; a part of the name is enough.",
          })}
        />
      )}
    </div>
  );
}
